import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocStepper } from 'commons/components/components-wrapper/poc-stepper.component';
import { ModalDialog } from 'commons/components';
import { CustomWorkflowComponent } from 'commons/components/custom-workflow-component';
import { CustomWorkflowStepComponent } from 'commons/components/custom-workflow-step-component';
import { NoClickableStep } from 'commons/components/step';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { executeRequest } from 'core/utils/backend-service';
import { showSuccessNotification } from 'core/utils/notification-service';
import { DELIMITERS } from 'modules/operators/pods/private-pods/lms-import/constants/constants';
import { useEffect, useMemo, useState } from 'react';
import { PopupProps } from 'react-popup-manager';
import { createCourseMappings } from '../service/lms-api-integration.service';
import {
    TrainingRegistrationMapping,
    TrainingRegistrationMappingData,
} from '../types/lms-training-registration-mapping.type';
import { TrainingRegistrationManagementMappings } from './training-registration-management-mappings.component';
import { TrainingRegistrationMappingFileUploadComponent } from './training-registration-mapping-file-upload.component';

const csvdelimiters: Record<string, string> = {
    comma: ',',
    semicolon: ';',
    tab: '\t',
};

const defaultMappings: TrainingRegistrationMapping[] = [
    { lmsField: '', pocField: 'lmsOperatorId', targetEntity: 'OPERATOR' },
    { lmsField: '', pocField: 'taskDetails', targetEntity: 'TRAINING' },
];

const fileTypes: string[] = ['.csv'];

type Step = {
    index: number;
    name: string;
    onPreviousStep: VoidFunction;
    disableNext?: boolean;
};

interface TrainingRegistrationMappingModalComponentProps extends PopupProps {
    lmsIntegrationMappingData?: TrainingRegistrationMappingData;
    onSaveMappings: VoidFunction;
}

export const TrainingRegistrationMappingModalComponent: React.FC<TrainingRegistrationMappingModalComponentProps> = (
    props
) => {
    const { isOpen, onClose, onSaveMappings, lmsIntegrationMappingData } = props;

    const { t } = useTranslation();

    const [filename, setFilename] = useState<string>('');
    const [delimiter, setDelimiter] = useState<string>(DELIMITERS.COMMA);
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);

    const [pocFieldsPresent, setPocFieldsPresent] = useState<boolean>(false);

    const onChangeStepUploadFileToMapping = () => setCurrentStep(steps.mapping);
    const onChangeStepMappingToUploadFile = () => setCurrentStep(steps.uploadFile);

    const steps: Record<string, Step> = {
        uploadFile: {
            index: 1,
            name: 'uploadFile',
            onPreviousStep: onClose!,
            disableNext: csvHeaders.length === 0,
        },
        mapping: {
            index: 2,
            name: 'mapping',
            onPreviousStep: onChangeStepMappingToUploadFile,
        },
    };

    const [currentStep, setCurrentStep] = useState<Step>(steps.uploadFile);
    const [mappings, setMappings] = useState<TrainingRegistrationMapping[]>(structuredClone(defaultMappings));

    const [fallback, setFallback] = useState<TrainingRegistrationMapping>(
        structuredClone({ ...defaultMappings[0], pocField: '' })
    );

    useEffect(() => {
        // Prefill mappings if exist
        if (lmsIntegrationMappingData?.mappings.length) {
            setMappings(structuredClone(lmsIntegrationMappingData.mappings));
        }
        // Prefill fallback if exists
        if (lmsIntegrationMappingData?.fallbacks.length) {
            setFallback(lmsIntegrationMappingData.fallbacks[0]);
        }
    }, []);

    useEffect(() => {
        if (currentStep.name === 'uploadFile') {
            setPocFieldsPresent(false);
            setCurrentStep({ ...currentStep, disableNext: !isValidFile });
        }
        if (currentStep.name === 'mapping') {
            setCurrentStep({ ...currentStep, disableNext: true });
        }
    }, [mappings, fallback, filename, currentStep.index]);

    const computeDisabledNextStep = () => {
        const hasIncompleteMapping = mappings.some((mapping) =>
            Object.values(mapping).some((value) => !value || value.length === 0)
        );
        const fallbackMismatch =
            (!fallback.lmsField && !!fallback.pocField) || (!!fallback.lmsField && !fallback.pocField);
        const assistiveTextExists =
            document.querySelector('owc-modal-dialog')!.querySelectorAll('owc-assistive-text').length > 0;

        return hasIncompleteMapping || fallbackMismatch || !pocFieldsPresent || assistiveTextExists;
    };

    useEffect(() => {
        // Trigger disable next button once pocFields are fetched
        if (currentStep.name === 'mapping') {
            setTimeout(() => {
                const disabledNextStep = computeDisabledNextStep();
                setCurrentStep({ ...currentStep, disableNext: disabledNextStep });
            }, 100);
        }
    }, [pocFieldsPresent, mappings, fallback]);

    const nextStepAction = useMemo(
        () =>
            t(
                `settings.integrations.lms-api-integration.mapping.modal.button.${
                    lmsIntegrationMappingData?.creationTime ? 'update' : 'create'
                }`
            ),
        [lmsIntegrationMappingData?.creationTime]
    );

    const getNextStep = () => [
        <PocButton
            key="change"
            variant="primary"
            size="medium"
            disabled={currentStep.disableNext ?? true}
            onClick={currentStep.index === 1 ? onChangeStepUploadFileToMapping : handleOnCreateMappings}
        >
            {currentStep.index === steps.uploadFile.index
                ? t('commons.components.custom-workflow.next-step')
                : nextStepAction}
        </PocButton>,
    ];

    const getPreviousStep = () => [
        <PocButton key="cancel" variant="tertiary" size="medium" onClick={steps.mapping.onPreviousStep}>
            {t('commons.components.custom-workflow.back-step')}
        </PocButton>,
    ];

    const handleLoadFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (!file) {
            return;
        }

        setFilename(file.name);

        file.text().then((text) => {
            const lines = text.split('\n');
            const firstLine = lines[0];
            const extractedHeaders = firstLine.split(csvdelimiters[delimiter]).map((header) => header.trim());
            setCsvHeaders(extractedHeaders);
        });
    };

    const isValidFile = useMemo<boolean>(() => {
        const fileSplit = filename.split('.');
        const fileExtension = `.${fileSplit.at(-1)}`;
        return fileTypes.includes(fileExtension);
    }, [filename]);

    const { mutate: createMappings } = usePocMutation({
        mutationFn: () =>
            executeRequest(() =>
                createCourseMappings({
                    fileReferenceName: filename,
                    mappings,
                    fallbacks: Object.values(fallback).every((value) => value && value.length > 0) ? [fallback] : [],
                })
            ),
        onSuccess: () => {
            showSuccessNotification(t('settings.integrations.lms-api-integration.mapping.notification.positive'));
        },
        onSettled: () => {
            onSaveMappings();
            onClose?.();
        },
    });

    const handleOnCreateMappings = () => createMappings();

    return (
        <ModalDialog
            title={t(
                `settings.integrations.lms-api-integration.mapping.modal.title.${
                    lmsIntegrationMappingData?.creationTime ? 'update' : 'create'
                }`
            )}
            isOpen={isOpen}
            size="medium"
            onClose={onClose}
            footerOptions={getNextStep()}
            prefixFooter={currentStep.index === steps.mapping.index ? getPreviousStep() : undefined}
        >
            <PocStepper activeStep={currentStep.index - 1} linear>
                <NoClickableStep
                    step={steps.uploadFile.index}
                    status={currentStep.index > steps.uploadFile.index ? 'complete' : 'in-progress'}
                >
                    {t('settings.integrations.lms-api-integration.mapping.stepper.step-1')}
                </NoClickableStep>
                <NoClickableStep
                    step={steps.mapping.index}
                    status={currentStep.index === steps.mapping.index ? 'in-progress' : 'default'}
                >
                    {t('settings.integrations.lms-api-integration.mapping.stepper.step-2')}
                </NoClickableStep>
            </PocStepper>
            <CustomWorkflowComponent currentStep={currentStep.name} showFooter={false}>
                <CustomWorkflowStepComponent step={steps.uploadFile.name}>
                    <TrainingRegistrationMappingFileUploadComponent
                        filename={filename}
                        delimiter={delimiter}
                        isValidFile={isValidFile}
                        onChangeDelimiter={(newDelimiter: string) => setDelimiter(newDelimiter)}
                        onLoadFile={(ev) => handleLoadFile(ev)}
                        onResetFile={() => {
                            setFilename('');
                            setCsvHeaders([]);
                        }}
                        isUploadOptionDisabled={!delimiter}
                        csvHeaders={csvHeaders}
                        fileTypes={fileTypes}
                        isUpdating={!!lmsIntegrationMappingData?.creationTime}
                    ></TrainingRegistrationMappingFileUploadComponent>
                </CustomWorkflowStepComponent>
                <CustomWorkflowStepComponent step={steps.mapping.name}>
                    <TrainingRegistrationManagementMappings
                        headers={csvHeaders}
                        mappings={mappings}
                        fallback={fallback}
                        setPocFieldsPresent={setPocFieldsPresent}
                        setMappings={setMappings}
                        setFallback={setFallback}
                    />
                </CustomWorkflowStepComponent>
            </CustomWorkflowComponent>
        </ModalDialog>
    );
};
